import type { ConfigResponse } from '../store/modules/merchant/types';
import { isConfigInheritedForMerchant } from './configInheritance';
import { getConfigDisplayLabel } from './parentSettingSave';

export type PartialPaymentValue = {
  enabled: boolean;
};

export const PARTIAL_PAYMENTS_CONFLICTING_CONFIGS = [
  'PARTIAL_AUTHORIZATIONS',
  'SPLIT_PAYMENTS',
  'GENERAL_FEE',
  'SURCHARGE_FEE',
  'DISCOUNTS',
  'TIPPING',
  'TIPPING_PERCENTAGES',
] as const;

export type PartialPaymentsConflictingConfigName =
  (typeof PARTIAL_PAYMENTS_CONFLICTING_CONFIGS)[number];

export const PARTIAL_PAYMENTS_MUTUAL_EXCLUSION_CONFIG_NAMES: readonly string[] = [
  'PARTIAL_PAYMENTS',
  ...PARTIAL_PAYMENTS_CONFLICTING_CONFIGS,
];

const PARTIAL_PAYMENTS_MUTUAL_EXCLUSION_CONFIG_NAME_SET = new Set(
  PARTIAL_PAYMENTS_MUTUAL_EXCLUSION_CONFIG_NAMES,
);

export const PARTIAL_PAYMENT_CONFLICT_MESSAGES = {
  PARTIAL_AUTHORIZATIONS:
    'Partial Authorization cannot be enabled while Allow Partial Payments is on. Turn off Allow Partial Payments first.',
  SPLIT_PAYMENTS:
    'Split Payments cannot be enabled while Allow Partial Payments is on. Turn off Allow Partial Payments first.',
  PARTIAL_PAYMENTS:
    'Allow Partial Payments cannot be enabled while Partial Authorization or Split Payments is on. Turn those options off first.',
  DISCOUNTS:
    'Discounts cannot be enabled while Allow Partial Payments is on. Turn off Allow Partial Payments first.',
  GENERAL_FEE:
    'General Fees cannot be enabled while Allow Partial Payments is on. Turn off Allow Partial Payments first.',
  SURCHARGE_FEE:
    'Surcharging Fees cannot be enabled while Allow Partial Payments is on. Turn off Allow Partial Payments first.',
  TIPPING_PERCENTAGES:
    'Tipping cannot be enabled while Allow Partial Payments is on. Turn off Allow Partial Payments first.',
} as const;

export type PartialPaymentsConfigState = {
  name: string;
  value?: unknown;
  enabled?: boolean;
  isDeleted?: boolean;
  isInherited?: boolean | null;
};

export type PartialPaymentMutualExclusionLocalState = {
  partialPaymentEnabled: boolean;
  partialAuthEnabled: boolean;
  splitPaymentsEnabled: boolean;
};

export type ConfigUpsertLike = {
  name: string;
  value?: unknown;
  enabled?: boolean;
};

export function isPartialPaymentEnabled(config?: { value?: PartialPaymentValue } | null): boolean {
  return config?.value?.enabled === true;
}

/** PARTIAL_AUTHORIZATIONS stores a boolean in `value`; treat missing value as disabled. */
export function isPartialAuthorizationEnabled(config?: { value?: boolean | null } | null): boolean {
  return config?.value === true;
}

export function isPartialPaymentsValueEnabled(value: unknown): boolean {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return (value as PartialPaymentValue).enabled === true;
}

export function isPartialPaymentsConfigActive(config: PartialPaymentsConfigState): boolean {
  return config.name === 'PARTIAL_PAYMENTS' && isPartialPaymentsValueEnabled(config.value);
}

export function isPartialPaymentsConflictingConfigActive(
  config: PartialPaymentsConfigState,
): boolean {
  switch (config.name) {
    case 'PARTIAL_AUTHORIZATIONS':
      return config.value === true;
    case 'SPLIT_PAYMENTS':
    case 'GENERAL_FEE':
    case 'SURCHARGE_FEE':
    case 'TIPPING':
    case 'TIPPING_PERCENTAGES':
      return config.enabled === true;
    case 'DISCOUNTS':
      return config.value === true;
    case 'PARTIAL_PAYMENTS':
      return isPartialPaymentsConfigActive(config);
    default:
      return false;
  }
}

export function partialPaymentsInheritanceConfigsConflict(
  existingConfigName: string,
  incomingConfigName: string,
): boolean {
  if (existingConfigName === incomingConfigName) {
    return false;
  }
  const conflicting = PARTIAL_PAYMENTS_CONFLICTING_CONFIGS as readonly string[];
  if (existingConfigName === 'PARTIAL_PAYMENTS' && conflicting.includes(incomingConfigName)) {
    return true;
  }
  if (incomingConfigName === 'PARTIAL_PAYMENTS' && conflicting.includes(existingConfigName)) {
    return true;
  }
  return false;
}

export function shouldBlockPartialPaymentsInheritanceFromParent(
  childConfigsAlreadyOnMerchant: PartialPaymentsConfigState[],
  parentConfig: PartialPaymentsConfigState,
): boolean {
  return childConfigsAlreadyOnMerchant.some(
    (childConfig) =>
      !childConfig.isDeleted &&
      partialPaymentsInheritanceConfigsConflict(childConfig.name, parentConfig.name),
  );
}

export function filterAllowedPartialPaymentsInheritedConfigs(
  ownConfigs: PartialPaymentsConfigState[],
  inheritedConfigs: PartialPaymentsConfigState[],
): PartialPaymentsConfigState[] {
  const configsForBlockCheck = [...ownConfigs];
  const allowedInherited: PartialPaymentsConfigState[] = [];

  for (const inherited of inheritedConfigs) {
    if (shouldBlockPartialPaymentsInheritanceFromParent(configsForBlockCheck, inherited)) {
      continue;
    }
    allowedInherited.push(inherited);
    if (PARTIAL_PAYMENTS_MUTUAL_EXCLUSION_CONFIG_NAME_SET.has(inherited.name)) {
      configsForBlockCheck.push(inherited);
    }
  }

  return allowedInherited;
}

function getChildOwnConfigs(configs: PartialPaymentsConfigState[]): PartialPaymentsConfigState[] {
  return configs.filter((config) => !config.isDeleted && !config.isInherited);
}

function previewOwnConfigsAfterMutation(
  ownConfigs: PartialPaymentsConfigState[],
  mutation: ConfigUpsertLike,
  existingOwnConfig?: PartialPaymentsConfigState,
): PartialPaymentsConfigState[] {
  const existing = existingOwnConfig ?? ownConfigs.find((config) => config.name === mutation.name);
  const preview: ConfigUpsertLike = {
    name: mutation.name,
    value: mutation.value !== undefined ? mutation.value : existing?.value,
    enabled: mutation.enabled !== undefined ? mutation.enabled : existing?.enabled,
  };
  const normalized = normalizePartialPaymentsEnabledOnCreate(preview);
  return [
    ...ownConfigs.filter((config) => config.name !== mutation.name),
    { ...normalized, isDeleted: false, isInherited: false },
  ];
}

/** Effective configs for mutual-exclusion checks (aligned with MMS hydrate + create/update). */
export function buildPartialPaymentsExclusionContext(
  hydratedConfigs: PartialPaymentsConfigState[],
  mutation?: ConfigUpsertLike,
  existingOwnConfig?: PartialPaymentsConfigState,
): PartialPaymentsConfigState[] {
  let ownConfigs = getChildOwnConfigs(hydratedConfigs);
  if (mutation) {
    ownConfigs = previewOwnConfigsAfterMutation(ownConfigs, mutation, existingOwnConfig);
  }

  const inheritedConfigs = hydratedConfigs.filter(
    (config) => !config.isDeleted && config.isInherited,
  );
  const allowedInherited = filterAllowedPartialPaymentsInheritedConfigs(
    ownConfigs,
    inheritedConfigs,
  );

  return [...ownConfigs, ...allowedInherited];
}

export function resolvesToEnablingPartialPayments(
  mutation: ConfigUpsertLike,
  existing?: PartialPaymentsConfigState,
): boolean {
  if (mutation.name !== 'PARTIAL_PAYMENTS' || mutation.value === undefined) {
    return false;
  }
  if (!isPartialPaymentsValueEnabled(mutation.value)) {
    return false;
  }
  return !existing || !isPartialPaymentsConfigActive(existing);
}

export function resolvesToEnablingConflictingConfig(
  mutation: ConfigUpsertLike,
  existing?: PartialPaymentsConfigState,
): boolean {
  if (
    !PARTIAL_PAYMENTS_CONFLICTING_CONFIGS.includes(
      mutation.name as PartialPaymentsConflictingConfigName,
    )
  ) {
    return false;
  }

  if (mutation.name === 'PARTIAL_AUTHORIZATIONS' || mutation.name === 'DISCOUNTS') {
    if (mutation.value !== true) {
      return false;
    }
    return existing?.value !== true;
  }

  if (
    mutation.name === 'SPLIT_PAYMENTS' ||
    mutation.name === 'GENERAL_FEE' ||
    mutation.name === 'SURCHARGE_FEE' ||
    mutation.name === 'TIPPING' ||
    mutation.name === 'TIPPING_PERCENTAGES'
  ) {
    if (mutation.enabled !== true) {
      return false;
    }
    return existing?.enabled !== true;
  }

  return false;
}

export function findActivePartialPaymentsConflict(
  configs: PartialPaymentsConfigState[],
  excludingConfigName?: string,
): string | null {
  for (const config of configs) {
    if (config.isDeleted) {
      continue;
    }
    if (excludingConfigName && config.name === excludingConfigName) {
      continue;
    }
    if (isPartialPaymentsConflictingConfigActive(config)) {
      return config.name;
    }
  }
  return null;
}

export function normalizePartialPaymentsEnabledOnCreate<T extends ConfigUpsertLike>(config: T): T {
  if (config.name !== 'PARTIAL_PAYMENTS') {
    return config;
  }
  return {
    ...config,
    enabled: isPartialPaymentsValueEnabled(config.value),
  };
}

export class PartialPaymentsConfigConflictError extends Error {
  constructor(
    public readonly enabling: string,
    public readonly conflicting: string,
  ) {
    super(formatPartialPaymentsConflictMessage(enabling, conflicting));
    this.name = 'PartialPaymentsConfigConflictError';
  }
}

export function formatPartialPaymentsConflictMessage(
  enabling: string,
  conflicting: string,
): string {
  const enablingLabel = getConfigDisplayLabel(enabling as ConfigResponse['name']);
  const conflictingLabel = getConfigDisplayLabel(conflicting as ConfigResponse['name']);
  return `Disable ${conflictingLabel} before enabling ${enablingLabel}.`;
}

export const CONFIG_SAVE_ERROR_FALLBACK =
  'An error occurred while saving these changes. Please try again later or contact Skipify Support';

export function assertPartialPaymentsMutualExclusion(
  configs: PartialPaymentsConfigState[],
  mutation: ConfigUpsertLike,
  existingConfig?: PartialPaymentsConfigState,
): void {
  if (resolvesToEnablingPartialPayments(mutation, existingConfig)) {
    const conflict = findActivePartialPaymentsConflict(configs, 'PARTIAL_PAYMENTS');
    if (conflict) {
      throw new PartialPaymentsConfigConflictError('PARTIAL_PAYMENTS', conflict);
    }
  }

  if (resolvesToEnablingConflictingConfig(mutation, existingConfig)) {
    const partialPaymentsEnabled = configs.some(
      (config) => !config.isDeleted && isPartialPaymentsConfigActive(config),
    );
    if (partialPaymentsEnabled) {
      throw new PartialPaymentsConfigConflictError(mutation.name, 'PARTIAL_PAYMENTS');
    }
  }
}

function applyMutationToHydratedStates(
  states: PartialPaymentsConfigState[],
  mutation: ConfigUpsertLike,
): PartialPaymentsConfigState[] {
  const normalized = normalizePartialPaymentsEnabledOnCreate(mutation);
  const withoutSameName = states.filter((state) => state.name !== normalized.name);
  return [
    ...withoutSameName,
    {
      name: normalized.name,
      value: normalized.value,
      enabled: normalized.enabled,
      isDeleted: false,
      isInherited: false,
    },
  ];
}

export function validatePartialPaymentsMutualExclusionBatch(
  hydratedConfigs: ConfigResponse[],
  merchantId: string,
  pendingUpserts: ConfigUpsertLike[],
): PartialPaymentsConfigConflictError | null {
  const relevantPending = pendingUpserts.filter((config) =>
    PARTIAL_PAYMENTS_MUTUAL_EXCLUSION_CONFIG_NAME_SET.has(config.name),
  );
  if (relevantPending.length === 0) {
    return null;
  }

  let states = mapMerchantConfigsToPartialPaymentsStates(hydratedConfigs, merchantId);
  const ordered = orderConfigsForPartialPaymentMutualExclusion(relevantPending);

  for (const upsert of ordered) {
    const mutation = normalizePartialPaymentsEnabledOnCreate(upsert);
    const existingOwn = getChildOwnConfigs(states).find((config) => config.name === mutation.name);
    const exclusionContext = buildPartialPaymentsExclusionContext(states, mutation, existingOwn);
    try {
      assertPartialPaymentsMutualExclusion(exclusionContext, mutation, existingOwn);
    } catch (err) {
      if (err instanceof PartialPaymentsConfigConflictError) {
        return err;
      }
      throw err;
    }
    states = applyMutationToHydratedStates(states, mutation);
  }

  return null;
}

export function mapMerchantConfigsToPartialPaymentsStates(
  configs: ConfigResponse[],
  merchantId: string,
  local?: PartialPaymentMutualExclusionLocalState,
): PartialPaymentsConfigState[] {
  const states: PartialPaymentsConfigState[] = [];

  for (const name of PARTIAL_PAYMENTS_MUTUAL_EXCLUSION_CONFIG_NAMES) {
    const config = configs.find((row) => row.name === name);
    if (config) {
      states.push(configRowToPartialPaymentsState(config, merchantId, local));
      continue;
    }

    if (!local) {
      continue;
    }

    if (name === 'PARTIAL_PAYMENTS' && local.partialPaymentEnabled) {
      states.push({
        name: 'PARTIAL_PAYMENTS',
        value: { enabled: true },
        enabled: true,
        isDeleted: false,
        isInherited: false,
      });
    } else if (name === 'PARTIAL_AUTHORIZATIONS' && local.partialAuthEnabled) {
      states.push({
        name: 'PARTIAL_AUTHORIZATIONS',
        value: true,
        enabled: true,
        isDeleted: false,
        isInherited: false,
      });
    } else if (name === 'SPLIT_PAYMENTS' && local.splitPaymentsEnabled) {
      states.push({
        name: 'SPLIT_PAYMENTS',
        value: { maxPayments: 2 },
        enabled: true,
        isDeleted: false,
        isInherited: false,
      });
    }
  }

  return states;
}

function configRowToPartialPaymentsState(
  config: ConfigResponse,
  merchantId: string,
  local?: PartialPaymentMutualExclusionLocalState,
): PartialPaymentsConfigState {
  const isInherited = isConfigInheritedForMerchant(merchantId, config);
  if (config.name === 'PARTIAL_PAYMENTS' && local) {
    return {
      name: config.name,
      value: { enabled: local.partialPaymentEnabled },
      enabled: local.partialPaymentEnabled,
      isDeleted: false,
      isInherited,
    };
  }
  if (config.name === 'PARTIAL_AUTHORIZATIONS' && local) {
    return {
      name: config.name,
      value: local.partialAuthEnabled,
      enabled: config.enabled,
      isDeleted: false,
      isInherited,
    };
  }
  if (config.name === 'SPLIT_PAYMENTS' && local) {
    return {
      name: config.name,
      value: config.value,
      enabled: local.splitPaymentsEnabled,
      isDeleted: false,
      isInherited,
    };
  }
  return {
    name: config.name,
    value: config.value,
    enabled: config.enabled,
    isDeleted: false,
    isInherited,
  };
}

function canEnableConfigWithMutualExclusion(
  hydratedConfigs: ConfigResponse[],
  merchantId: string,
  local: PartialPaymentMutualExclusionLocalState,
  mutation: ConfigUpsertLike,
): boolean {
  const states = mapMerchantConfigsToPartialPaymentsStates(hydratedConfigs, merchantId, local);
  const existingOwn = getChildOwnConfigs(states).find((config) => config.name === mutation.name);
  const exclusionContext = buildPartialPaymentsExclusionContext(states, mutation, existingOwn);
  try {
    assertPartialPaymentsMutualExclusion(exclusionContext, mutation, existingOwn);
    return true;
  } catch {
    return false;
  }
}

function formatToggleConflictMessage(enabling: string, conflicting: string): string {
  if (enabling === 'PARTIAL_PAYMENTS') {
    if (conflicting === 'PARTIAL_AUTHORIZATIONS') {
      return 'Allow Partial Payments cannot be enabled while Partial Authorization is on. Turn off Partial Authorization first.';
    }
    if (conflicting === 'SPLIT_PAYMENTS') {
      return 'Allow Partial Payments cannot be enabled while Split Payments is on. Turn off Split Payments first.';
    }
    return `Allow Partial Payments cannot be enabled while ${getConfigDisplayLabel(
      conflicting as ConfigResponse['name'],
    )} is on. Turn that setting off first.`;
  }
  if (enabling === 'PARTIAL_AUTHORIZATIONS') {
    return PARTIAL_PAYMENT_CONFLICT_MESSAGES.PARTIAL_AUTHORIZATIONS;
  }
  if (enabling === 'SPLIT_PAYMENTS') {
    return PARTIAL_PAYMENT_CONFLICT_MESSAGES.SPLIT_PAYMENTS;
  }
  return formatPartialPaymentsConflictMessage(enabling, conflicting);
}

export function getPartialPaymentToggleConflict(
  configs: ConfigResponse[],
  merchantId: string,
  local: PartialPaymentMutualExclusionLocalState,
): string | null {
  if (local.partialPaymentEnabled) {
    return null;
  }

  const mutation = {
    name: 'PARTIAL_PAYMENTS',
    value: { enabled: true },
    enabled: true,
  };
  if (canEnableConfigWithMutualExclusion(configs, merchantId, local, mutation)) {
    return null;
  }

  const states = mapMerchantConfigsToPartialPaymentsStates(configs, merchantId, local);
  const existingOwn = getChildOwnConfigs(states).find(
    (config) => config.name === 'PARTIAL_PAYMENTS',
  );
  const exclusionContext = buildPartialPaymentsExclusionContext(states, mutation, existingOwn);
  return findActivePartialPaymentsConflict(exclusionContext, 'PARTIAL_PAYMENTS');
}

export function getPartialPaymentToggleDisabledReason(
  configs: ConfigResponse[],
  merchantId: string,
  local: PartialPaymentMutualExclusionLocalState,
): string | null {
  if (local.partialPaymentEnabled) {
    return null;
  }

  const mutation = {
    name: 'PARTIAL_PAYMENTS',
    value: { enabled: true },
    enabled: true,
  };
  if (canEnableConfigWithMutualExclusion(configs, merchantId, local, mutation)) {
    return null;
  }

  const conflict = getPartialPaymentToggleConflict(configs, merchantId, local);
  if (conflict) {
    return formatToggleConflictMessage('PARTIAL_PAYMENTS', conflict);
  }
  return PARTIAL_PAYMENT_CONFLICT_MESSAGES.PARTIAL_PAYMENTS;
}

export function getPartialAuthToggleDisabledReason(
  configs: ConfigResponse[],
  merchantId: string,
  local: PartialPaymentMutualExclusionLocalState,
): string | null {
  if (local.partialAuthEnabled) {
    return null;
  }

  const mutation = { name: 'PARTIAL_AUTHORIZATIONS', value: true, enabled: true };
  if (canEnableConfigWithMutualExclusion(configs, merchantId, local, mutation)) {
    return null;
  }
  return PARTIAL_PAYMENT_CONFLICT_MESSAGES.PARTIAL_AUTHORIZATIONS;
}

export function getSplitPaymentsToggleDisabledReason(
  configs: ConfigResponse[],
  merchantId: string,
  local: PartialPaymentMutualExclusionLocalState,
): string | null {
  if (local.splitPaymentsEnabled) {
    return null;
  }

  const mutation = { name: 'SPLIT_PAYMENTS', enabled: true, value: { maxPayments: 2 } };
  if (canEnableConfigWithMutualExclusion(configs, merchantId, local, mutation)) {
    return null;
  }
  return PARTIAL_PAYMENT_CONFLICT_MESSAGES.SPLIT_PAYMENTS;
}

export type PartialPaymentConfigRow = ConfigResponse<PartialPaymentValue>;

/** Partial payments is on and set on this merchant (not inherited from a parent). */
export function isOwnedPartialPaymentEnabled(
  partialPaymentConfig: PartialPaymentConfigRow | undefined | null,
  merchantId: string,
): boolean {
  if (!partialPaymentConfig || !isPartialPaymentEnabled(partialPaymentConfig)) {
    return false;
  }
  return !isConfigInheritedForMerchant(merchantId, partialPaymentConfig);
}

/** Block enabling fees/discounts when this merchant owns an active partial-payments setting. */
export function getFeeOrDiscountToggleDisabledReasonForOwnedPartialPayments(
  partialPaymentConfig: PartialPaymentConfigRow | undefined | null,
  merchantId: string,
  localSettingEnabled: boolean,
  blockedSetting: 'DISCOUNTS' | 'GENERAL_FEE' | 'SURCHARGE_FEE' | 'TIPPING_PERCENTAGES',
): string | null {
  if (localSettingEnabled) {
    return null;
  }
  if (!isOwnedPartialPaymentEnabled(partialPaymentConfig, merchantId)) {
    return null;
  }
  return PARTIAL_PAYMENT_CONFLICT_MESSAGES[blockedSetting];
}

function pendingUpsertDisablesConfig(upsert: ConfigUpsertLike): boolean {
  if (upsert.name === 'PARTIAL_PAYMENTS') {
    return upsert.value !== undefined && !isPartialPaymentsValueEnabled(upsert.value);
  }
  if (upsert.name === 'PARTIAL_AUTHORIZATIONS') {
    return upsert.value === false;
  }
  if (upsert.name === 'SPLIT_PAYMENTS') {
    return upsert.enabled === false;
  }
  return false;
}

/** Save-bar detail when enabling a setting drops an inherited conflicting parent value (MMS inheritance rules). */
export function getPartialPaymentsInheritedConflictSaveDetailText(
  configs: ConfigResponse[],
  merchantId: string,
  pendingUpserts: ConfigUpsertLike[],
): string | null {
  const enablingUpserts = pendingUpserts.filter(
    (upsert) =>
      resolvesToEnablingPartialPayments(upsert) || resolvesToEnablingConflictingConfig(upsert),
  );
  if (enablingUpserts.length === 0) {
    return null;
  }

  const messages: string[] = [];

  for (const upsert of enablingUpserts) {
    const states = mapMerchantConfigsToPartialPaymentsStates(configs, merchantId);
    const inheritedActive = states.filter(
      (state) =>
        state.isInherited &&
        isPartialPaymentsConflictingConfigActive(state) &&
        partialPaymentsInheritanceConfigsConflict(state.name, upsert.name),
    );

    for (const inherited of inheritedActive) {
      const disabledInSameSave = pendingUpserts.some(
        (pending) => pending.name === inherited.name && pendingUpsertDisablesConfig(pending),
      );
      if (disabledInSameSave) {
        continue;
      }

      const enablingLabel = getConfigDisplayLabel(upsert.name as ConfigResponse['name']);
      const inheritedLabel = getConfigDisplayLabel(inherited.name as ConfigResponse['name']);
      messages.push(
        `Saving enables ${enablingLabel} for this merchant. Inherited ${inheritedLabel} from your parent will no longer apply here.`,
      );
    }
  }

  return messages.length > 0
    ? messages.filter((msg, i, arr) => arr.indexOf(msg) === i).join(' ')
    : null;
}

/** Save-bar detail when one save both disables and enables paired mutual-exclusion settings. */
export function getPartialPaymentsPairedChangeSaveDetailText(
  pendingUpserts: ConfigUpsertLike[],
): string | null {
  const disablesPartialPayments = pendingUpserts.some(
    (pending) => pending.name === 'PARTIAL_PAYMENTS' && pendingUpsertDisablesConfig(pending),
  );
  const enablesPartialPayments = pendingUpserts.some((pending) =>
    resolvesToEnablingPartialPayments(pending),
  );
  const disablesPartialAuth = pendingUpserts.some(
    (pending) => pending.name === 'PARTIAL_AUTHORIZATIONS' && pendingUpsertDisablesConfig(pending),
  );
  const enablesPartialAuth = pendingUpserts.some(
    (pending) =>
      pending.name === 'PARTIAL_AUTHORIZATIONS' && resolvesToEnablingConflictingConfig(pending),
  );
  const disablesSplit = pendingUpserts.some(
    (pending) => pending.name === 'SPLIT_PAYMENTS' && pendingUpsertDisablesConfig(pending),
  );
  const enablesSplit = pendingUpserts.some(
    (pending) => pending.name === 'SPLIT_PAYMENTS' && resolvesToEnablingConflictingConfig(pending),
  );

  if (disablesPartialAuth && enablesPartialPayments) {
    return 'Saving turns off Partial Authorization and enables Partial Payments for this merchant.';
  }
  if (disablesPartialPayments && enablesPartialAuth) {
    return 'Saving turns off Partial Payments and enables Partial Authorization for this merchant.';
  }
  if (disablesPartialPayments && enablesSplit) {
    return 'Saving turns off Partial Payments and enables Split Payments for this merchant.';
  }
  if (disablesSplit && enablesPartialPayments) {
    return 'Saving turns off Split Payments and enables Partial Payments for this merchant.';
  }

  return null;
}

/** When saving multiple payment configs at once, apply disables before enables to satisfy MMS mutual exclusion. */
export function orderConfigsForPartialPaymentMutualExclusion<T extends ConfigUpsertLike>(
  configs: T[],
): T[] {
  const tier = (config: ConfigUpsertLike): number => {
    if (config.name === 'PARTIAL_PAYMENTS') {
      const enabled = (config.value as PartialPaymentValue | undefined)?.enabled;
      if (enabled === false) {
        return 0;
      }
      if (enabled === true) {
        return 2;
      }
    }
    if (config.name === 'PARTIAL_AUTHORIZATIONS') {
      if (config.value === false) {
        return 0;
      }
      if (config.value === true) {
        return 2;
      }
    }
    if (config.name === 'SPLIT_PAYMENTS') {
      if (config.enabled === false) {
        return 0;
      }
      if (config.enabled === true) {
        return 2;
      }
    }
    return 1;
  };

  return [...configs].sort((a, b) => tier(a) - tier(b));
}
